import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const MessageList = ({ messages, loading, onNew, onEdit, onDelete, onToggleActive }) => {
  const getTypeColor = (type) => {
    const colors = {
      success: 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
      info: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
      warning: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
      error: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'
    };
    return colors[type] || colors.info;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isActive = (message) => {
    if (!message.active) return false;
    const now = new Date();
    const start = new Date(message.start_date);
    const end = message.end_date ? new Date(message.end_date) : null;
    return start <= now && (!end || end >= now);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-foreground">All Messages</h2>
          <p className="text-sm text-muted-foreground">
            {messages.length} total message{messages.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={onNew} className="flex items-center gap-2">
          <Icon name="Plus" size={16} />
          New Message
        </Button>
      </div>

      {/* Messages List */}
      {messages.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <Icon name="MessageSquare" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-4">No messages yet</p>
          <Button onClick={onNew}>Create your first message</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((message) => (
            <div
              key={message.id}
              className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Message Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-foreground truncate">
                      {message.title}
                    </h3>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeColor(
                        message.message_type
                      )}`}
                    >
                      {message.message_type}
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-muted text-muted-foreground">
                      {message.variant}
                    </span>
                    {isActive(message) ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                        Inactive
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {message.content}
                  </p>

                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Target" size={14} />
                      {message.target_audience.join(', ')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Layout" size={14} />
                      {message.display_pages.join(', ')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      {formatDate(message.start_date)}
                      {message.end_date && ` - ${formatDate(message.end_date)}`}
                    </span>
                    {message.priority > 0 && (
                      <span className="flex items-center gap-1">
                        <Icon name="Star" size={14} />
                        Priority: {message.priority}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onToggleActive(message.id, message.active)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title={message.active ? 'Deactivate' : 'Activate'}
                  >
                    <Icon
                      name={message.active ? 'Eye' : 'EyeOff'}
                      size={18}
                      className="text-muted-foreground"
                    />
                  </button>
                  <button
                    onClick={() => onEdit(message)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Icon name="Edit" size={18} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => onDelete(message.id)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Icon name="Trash2" size={18} className="text-red-600 dark:text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageList;
